import type { State } from '../configureStore'
import * as AlertActionTypes from '../actionTypes/alerts.action.types'
import * as UserUsageActions from './userUsage.actions'
import type { AlertItem, AlertDetails } from '../reducers/alerts/default.state'
import * as ReduxHelpers from '@src/utils/reduxHelpers'
import * as alertproto from '@src/endpoints/services/intentMgr.service'
import {
  AlertStatusEnum,
  ResourceTypeEnum,
  AlertSeverityEnum
} from '@src/endpoints/services/commonHdr.service'
import { toastError, toastSuccess, jsonify } from '@src/utils/toast'
import uuidv4 from 'uuid/v4'
import { correctEmailFormat } from '@src/utils/accounts'
import {
  parseAlertDetails,
  getAlertTabs,
  AlertTab
} from '@src/utils/alert.utils'
import { getDate } from '@src/utils/date.helpers'
import { AppStorage } from '@src/utils/storage.helpers'
import { storagePrefix } from '@src/constants/constants'
import { paramsWithFilter } from '@src/endpoints/helpers'
import { isEmpty } from 'lodash'
import { subHours } from 'date-fns'

// Action Creators
export type ActionTypes =
  | getAlertsListSuccessAction
  | getAlertDetailsSuccessAction
  | getAlertsListFailAction
  | getAlertDetailsFailAction
  | requestPendingAction
  | requestPendingWithGraphAction
  | deleteAlertSuccessAction
  | deleteAlertFailAction
  | updateAlertSuccessAction
  | updateAlertFailAction
  | disposeAlertAction
  | disposeAlertsAction

const getAlertsListSuccess = (
  alertsList: AlertItem[],
  alertTabs: AlertTab[],
  pagesCount: number,
  highCount: number,
  mediumCount: number,
  lowCount: number,
  totalCount: number,
  openCount: number,
  isPending = false
) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.GET_ALERTS_LIST_SUCCESS,
    {
      alertsList,
      alertTabs,
      pagesCount,
      highCount,
      mediumCount,
      lowCount,
      totalCount,
      openCount,
      isPending
    }
  )
type getAlertsListSuccessAction = ReturnType<typeof getAlertsListSuccess>

const getAlertDetailsSuccess = (alertDetail: AlertDetails, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.GET_ALERT_DETAILS_SUCCESS,
    {
      alertDetail,
      isPending
    }
  )
type getAlertDetailsSuccessAction = ReturnType<typeof getAlertDetailsSuccess>

const getAlertsListFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.GET_ALERTS_LIST_FAIL,
    {
      error
    }
  )

type getAlertsListFailAction = ReturnType<typeof getAlertsListFail>

const getAlertDetailsFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.GET_ALERT_DETAILS_FAIL,
    {
      error
    }
  )
type getAlertDetailsFailAction = ReturnType<typeof getAlertDetailsFail>

const requestPending = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(AlertActionTypes.REQUEST_PENDING, {
    isPending
  })
type requestPendingAction = ReturnType<typeof requestPending>

const requestPendingWithGraph = (isPending: boolean) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.REQUEST_PENDING_WITH_GRAPH,
    {
      isPending
    }
  )
type requestPendingWithGraphAction = ReturnType<typeof requestPendingWithGraph>

const deleteAlertSuccess = (id: string, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.DELETE_ALERT_SUCCESS,
    {
      id,
      isPending
    }
  )
type deleteAlertSuccessAction = ReturnType<typeof deleteAlertSuccess>

const deleteAlertFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(AlertActionTypes.DELETE_ALERT_FAIL, {
    error
  })
type deleteAlertFailAction = ReturnType<typeof deleteAlertFail>

const updateAlertSuccess = (id: string, status: any, isPending = false) =>
  ReduxHelpers.Actions.createPayloadAction(
    AlertActionTypes.UPDATE_ALERT_SUCCESS,
    {
      id,
      status,
      isPending
    }
  )
type updateAlertSuccessAction = ReturnType<typeof updateAlertSuccess>

const updateAlertFail = (error: any) =>
  ReduxHelpers.Actions.createPayloadAction(AlertActionTypes.UPDATE_ALERT_FAIL, {
    error
  })
type updateAlertFailAction = ReturnType<typeof updateAlertFail>

export const disposeAlert = () =>
  ReduxHelpers.Actions.createAction(AlertActionTypes.DISPOSE_ALERT)
type disposeAlertAction = ReturnType<typeof disposeAlert>

export const disposeAlerts = () =>
  ReduxHelpers.Actions.createAction(AlertActionTypes.DISPOSE_ALERTS)
type disposeAlertsAction = ReturnType<typeof disposeAlerts>

export function deleteAlert(id: string) {
  const params = { alert_id: id }
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    alertproto.IntentMgr.DeleteAlert({ params }).then(res => {
      if (!isEmpty(res.error)) {
        toastError(jsonify(res.error))
        dispatch(deleteAlertFail(res.error))
      } else {
        dispatch(deleteAlertSuccess(res as any))
        dispatch(getAlertsList())
      }
      dispatch(requestPending(false))
    })
  }
}

export function updateAlert({
  status,
  checkedAlerts,
  singleUpdate,
  callback
}: {
  status?: AlertStatusEnum
  checkedAlerts?: string[]
  singleUpdate?: boolean
  callback?: () => void
}) {
  const storage = new AppStorage(storagePrefix, sessionStorage)
  const userEmail = storage.get('userEmail')
  const params: alertproto.UpdateAlertReq = {
    req_hdr: {
      tid: uuidv4()
    },
    update_alert_info_list: checkedAlerts.map(alertId => ({
      user_id: correctEmailFormat(userEmail),
      status,
      alert_id: alertId
    }))
  }
  return (dispatch: any, getState: () => State) => {
    dispatch(requestPending(true))
    alertproto.IntentMgr.UpdateAlert({ params }).then(res => {
      if (!isEmpty(res.error)) {
        dispatch(updateAlertFail(res.error))
        toastError(jsonify(res.error))
      } else {
        if (!singleUpdate) {
          toastSuccess('Success')
        }
        dispatch(updateAlertSuccess(res as any, status))
        if (callback) callback()
      }
      dispatch(requestPending(false))
    })
  }
}

export function getAlertDetails(
  alertId: string,
  callback?: (data?: alertproto.AlertsInfo[]) => void
) {
  return (dispatch: any, getState: () => State) => {
    const { filter } = getState()
    const params = { alert_id: alertId }
    dispatch(requestPendingWithGraph(true))
    alertproto.IntentMgr.GetAlertNew({
      params: paramsWithFilter(params, filter)
    }).then(res => {
      if (!isEmpty(res.error)) {
        toastError(jsonify(res.error))
        dispatch(getAlertDetailsFail(res.error))
      } else {
        const alerts = res.alert_info_list || []
        const parsedAlert = parseAlertDetails(alerts[0]) || ({} as AlertDetails)
        const { start_time, end_time, hasQuery } = (parsedAlert ||
          {}) as AlertDetails
        if (hasQuery) {
          dispatch(
            UserUsageActions.setTimestamp({
              startTime: start_time
                ? getDate(start_time)
                : subHours(getDate(parsedAlert.alert_time), 2),
              endTime: end_time
                ? getDate(end_time)
                : getDate(parsedAlert.alert_time)
            })
          )
        }

        dispatch(getAlertDetailsSuccess(parsedAlert))
        if (callback) callback(alerts)
      }
    })
  }
}

interface GetAlertsListOptions {
  resourceType?: ResourceTypeEnum // will add condition for resource type in object details
  resourceName?: string // will add condition for resource name in object details
  accountId?: string //  will add condition for account in object details
  changeId?: string //  will add codition for alert-change link
  severity?: AlertSeverityEnum //  will add codition for alert severity
  statuses?: AlertStatusEnum[] //  will add codition for alert statuses
  //!!!if passed resource type and name will be returned all alerts except closed
  alertType?: alertproto.AlertTypeEnum // will add condition for alert type
  entityType?: string //  will add condition for alert entity type
  pageSize?: number //  will add logic for server paging
  pageNumber?: number //  will add logic for server paging
  sortField?: string //  will add logic for server sorting
  asc?: boolean //  will add logic for server sorting
  searchValue?: string //  will add condition for server filtering to search this
  searchFields?: string[] //  will add logic for server filtering by these fields
  callback?: (a?: any, b?: any) => void // callback if no error
  graphClickData?: () => void // data passed to callback
}

export function getAlertsList(options?: GetAlertsListOptions) {
  return (dispatch: any, getState: () => State) => {
    const {
      resourceType = ResourceTypeEnum.UNKNOWN,
      resourceName = '',
      accountId = '',
      changeId = '',
      statuses = [],
      severity = AlertSeverityEnum.ALERT_SEVERITY_NONE,
      alertType = alertproto.AlertTypeEnum.ALERT_TYPE_NONE,
      entityType = '',
      pageSize = 20,
      pageNumber = 0,
      sortField = '',
      asc,
      searchValue = '',
      searchFields = [],
      callback
    } = (options || {}) as GetAlertsListOptions
    const { filter, shell } = getState()
    const { resourceTypes } = shell
    const params = {
      'table_options.page_number': pageNumber,
      'table_options.page_size': pageSize,
      'table_options.sorted_field': sortField,
      'table_options.sorted_asc': !!asc,
      'table_options.search_filter': searchValue,
      'table_options.search_fields': searchFields,
      alert_status: statuses,
      rsrc_type: resourceType,
      rsrc_name: resourceName,
      change_id: changeId,
      alert_type: alertType,
      entity_type: entityType,
      severity
    }
    dispatch(requestPending(true))

    alertproto.IntentMgr.GetAlertNew({
      params: paramsWithFilter(params, filter, { account_id: accountId })
    }).then(res => {
      if (!isEmpty(res.error)) {
        toastError(jsonify(res.error))
        dispatch(getAlertsListFail(res.error))
      } else {
        const list: AlertItem[] = (res.alert_info_list || []).map(item => {
          const alert: AlertItem = { ...item }
          if (
            typeof alert.entity_type !== 'undefined' &&
            Array.isArray(alert.entity_type)
          ) {
            const index = alert.entity_type.indexOf('UNKNOWN')
            if (index !== -1) {
              alert.entity_type.splice(index, 1)
            }
            alert.entityTypes = item.entity_type.join(', ')
          }
          return alert
        })

        const total = res.count || {}

        const alertTabs = getAlertTabs(
          res.type_count_list,
          res.alert_count_list,
          resourceTypes
        )

        dispatch(
          getAlertsListSuccess(
            list,
            resourceType && resourceName ? [] : alertTabs,
            res.pages_count,
            total.high_count,
            total.medium_count,
            total.low_count,
            total.total_count,
            total.open_count
          )
        )
        if (callback) callback(list, null)
      }
      dispatch(requestPending(false))
    })
  }
}
